import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody } from '../../../components/card/card.jsx';
import { useCronExecutions } from '../../../hooks/useCronTasks.js';
import cronService from '../../../services/cronService.js';

/**
 * Component for displaying cron task execution history
 */
const CronExecutionHistory = ({ 
  taskId, 
  taskName,
  onClose,
  onViewResults
}) => {
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  const {
    executions,
    isLoading,
    error,
    loadExecutions,
    hasMore,
    loadMore
  } = useCronExecutions(taskId);
  
  // Load executions when component mounts
  useEffect(() => {
    if (taskId) {
      loadExecutions();
    }
  }, [taskId, loadExecutions]);
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    const statusColors = cronService.getStatusColor(status);
    return `badge bg-${statusColors.bg} text-${statusColors.text}`;
  };
  
  // Format duration
  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    if (!endTime) return 'Running...';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationSec = Math.round(durationMs / 1000);
    
    if (durationSec < 60) {
      return `${durationSec}s`;
    } else if (durationSec < 3600) {
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;
      return `${minutes}m ${seconds}s`;
    } else {
      const hours = Math.floor(durationSec / 3600);
      const minutes = Math.floor((durationSec % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };
  
  // Handle execution selection
  const handleExecutionClick = (execution) => {
    setSelectedExecution(execution);
    setShowDetails(true);
  };
  
  // Handle view results
  const handleViewResults = (execution) => {
    if (onViewResults) {
      window.location.href = `/small-pentest`;
    }
  };
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Execution History - {taskName}
            </h4>
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Failed to load execution history: {error}
          </div>
        </CardBody>
      </Card>
    );
  }
  
  return (
    <div className="cron-execution-history">
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Execution History - {taskName}
            </h4>
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        </CardHeader>
        <CardBody>
          {isLoading && executions.length === 0 ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading execution history...</div>
            </div>
          ) : executions.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-clock-history display-4 text-muted mb-3"></i>
              <h5 className="text-muted">No Executions Found</h5>
              <p className="text-muted mb-0">
                This task hasn't been executed yet.
              </p>
            </div>
          ) : (
            <>
              {/* Execution List */}
              <div className="execution-list">
                {executions.map((execution) => (
                  <div 
                    key={execution.id} 
                    className="border rounded p-3 mb-3 cursor-pointer hover-bg-light"
                    onClick={() => handleExecutionClick(execution)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="row align-items-center">
                      <div className="col-md-3">
                        <div className="d-flex align-items-center">
                          <span className={getStatusBadgeClass(execution.status)}>
                            {execution.status.charAt(0).toUpperCase() + execution.status.slice(1)}
                          </span>
                          {execution.status === 'running' && (
                            <div className="spinner-border spinner-border-sm ms-2" role="status"></div>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-md-3">
                        <div className="small text-muted">Started</div>
                        <div>{cronService.formatDate(execution.startTime)}</div>
                      </div>
                      
                      <div className="col-md-2">
                        <div className="small text-muted">Duration</div>
                        <div>{formatDuration(execution.startTime, execution.endTime)}</div>
                      </div>
                      
                      <div className="col-md-2">
                        <div className="small text-muted">Tools</div>
                        <div>{execution.toolsUsed?.length || 0}</div>
                      </div>
                      
                      <div className="col-md-2 text-end">
                        {execution.status === 'completed' && execution.results && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewResults(execution);
                            }}
                            title="View scan results"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Progress bar for running executions */}
                    {execution.status === 'running' && execution.progress !== undefined && (
                      <div className="mt-2">
                        <div className="progress" style={{ height: '6px' }}>
                          <div 
                            className="progress-bar" 
                            style={{ width: `${execution.progress}%` }}
                          ></div>
                        </div>
                        <small className="text-muted">Progress: {execution.progress}%</small>
                      </div>
                    )}
                    
                    {/* Error message for failed executions */}
                    {execution.status === 'failed' && execution.error && (
                      <div className="mt-2">
                        <div className="alert alert-danger alert-sm mb-0">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          <small>{execution.error}</small>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Load More Button */}
              {hasMore && (
                <div className="text-center">
                  <button
                    className="btn btn-outline-primary"
                    onClick={loadMore}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-down me-2"></i>
                        Load More
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
      
      {/* Execution Details Modal */}
      {showDetails && selectedExecution && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-info-circle me-2"></i>
                  Execution Details
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDetails(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Status:</strong>
                    <span className={`ms-2 ${getStatusBadgeClass(selectedExecution.status)}`}>
                      {selectedExecution.status.charAt(0).toUpperCase() + selectedExecution.status.slice(1)}
                    </span>
                  </div>
                  <div className="col-md-6">
                    <strong>Execution ID:</strong>
                    <span className="ms-2 font-monospace">{selectedExecution.id}</span>
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Started:</strong>
                    <span className="ms-2">{cronService.formatDate(selectedExecution.startTime)}</span>
                  </div>
                  <div className="col-md-6">
                    <strong>Duration:</strong>
                    <span className="ms-2">{formatDuration(selectedExecution.startTime, selectedExecution.endTime)}</span>
                  </div>
                </div>
                
                {selectedExecution.toolsUsed && selectedExecution.toolsUsed.length > 0 && (
                  <div className="mb-3">
                    <strong>Tools Used:</strong>
                    <div className="mt-2">
                      {selectedExecution.toolsUsed.map((tool, index) => (
                        <span key={index} className="badge bg-secondary me-2 mb-1">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedExecution.targetUrl && (
                  <div className="mb-3">
                    <strong>Target URL:</strong>
                    <span className="ms-2">{selectedExecution.targetUrl}</span>
                  </div>
                )}
                
                {selectedExecution.logs && selectedExecution.logs.length > 0 && (
                  <div className="mb-3">
                    <strong>Execution Logs:</strong>
                    <div className="mt-2">
                      <div className="bg-dark text-light p-3 rounded font-monospace small" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {selectedExecution.logs.map((log, index) => (
                          <div key={index} className="mb-1">
                            <span className="text-muted">[{cronService.formatDate(log.timestamp)}]</span>
                            <span className={`ms-2 ${log.level === 'error' ? 'text-danger' : log.level === 'warn' ? 'text-warning' : 'text-light'}`}>
                              {log.message}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedExecution.error && (
                  <div className="mb-3">
                    <strong>Error:</strong>
                    <div className="alert alert-danger mt-2">
                      {selectedExecution.error}
                    </div>
                  </div>
                )}
                
                {selectedExecution.results && (
                  <div className="mb-3">
                    <strong>Results Summary:</strong>
                    <div className="mt-2">
                      <div className="row text-center">
                        <div className="col-3">
                          <div className="fw-bold text-info">{selectedExecution.results.totalFindings || 0}</div>
                          <div className="small text-muted">Total Findings</div>
                        </div>
                        <div className="col-3">
                          <div className="fw-bold text-danger">{selectedExecution.results.highSeverity || 0}</div>
                          <div className="small text-muted">High Severity</div>
                        </div>
                        <div className="col-3">
                          <div className="fw-bold text-warning">{selectedExecution.results.mediumSeverity || 0}</div>
                          <div className="small text-muted">Medium Severity</div>
                        </div>
                        <div className="col-3">
                          <div className="fw-bold text-success">{selectedExecution.results.lowSeverity || 0}</div>
                          <div className="small text-muted">Low Severity</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {selectedExecution.status === 'completed' && selectedExecution.results && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      handleViewResults(selectedExecution);
                      setShowDetails(false);
                    }}
                  >
                    <i className="bi bi-eye me-2"></i>
                    View Full Results
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDetails(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CronExecutionHistory;